import type { ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { Table, TableWrapper } from "@/components/ui/table";

/**
 * Read-only batch release plan observation (ALL-7260).
 *
 * This is a normalized presentation port, not a deployed wire DTO: the caller supplies an
 * observation that a future governed server read would produce. The component never calls the
 * network, never derives or sums values, and never turns plan approval, canary acceptance or a
 * release receipt into authority to execute anything. Exact amounts and identifiers are rendered
 * as received.
 */
export interface BatchReleasePlanObservation {
  batchId: string;
  planId: string;
  planVersion: number;
  planDigest: string;
  sourceSnapshotId: string;
  scopeDigest: string;
  assetIdentity: {
    chain: string;
    network: string;
    contractAddress: string;
    decimals: number;
    tokenStandard: string;
    symbol?: string;
  };
  valueCap: string;
  windowStart: string;
  windowEnd: string;
  sourceWalletRefs: string[];
  hasMore: boolean;
  rows: { transferId: string; sourceWalletRef: string; targetRef: string; state: string }[];
  canaryOutcome: null | { accepted: boolean; acceptedByRef?: string; acceptedAt?: string; reasonCode?: string };
  release: null | { releasedByRef: string; releasedAt: string; approvalRef: string };
}

export type BatchReleasePlanState =
  | { status: "loading" }
  | { status: "unavailable" | "refused"; reasonCode: string; supportReference?: string }
  | { status: "observed"; data: BatchReleasePlanObservation };

type ObservationRow = BatchReleasePlanObservation["rows"][number];

const EXECUTION_UNAVAILABLE = "BATCH_RELEASE_EXECUTION_UNAVAILABLE";

function nonBlank(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

/**
 * Exact decimal string: the raw value is validated unchanged — no trim, no float parsing, no
 * rounding, no exponent. The fractional scale must equal the declared decimals exactly, so a
 * zero-decimal asset carries no decimal point.
 */
function isExactAmount(value: unknown, decimals: unknown): value is string {
  if (typeof value !== "string") return false;
  if (!/^(0|[1-9][0-9]*)(\.[0-9]+)?$/.test(value)) return false;
  if (typeof decimals !== "number" || !Number.isInteger(decimals) || decimals < 0 || decimals > 36) {
    return false;
  }
  const dot = value.indexOf(".");
  const fraction = dot === -1 ? "" : value.slice(dot + 1);
  return fraction.length === decimals;
}

/**
 * Strict RFC 3339 date-time with an explicit offset (Z or ±hh:mm). Loose strings such as
 * "October 17, 2026" or "2026-10-17 10:00" are rejected before Date.parse sees them, so they take
 * the malformed path instead of being read in the viewer's local time zone.
 */
const RFC3339_TIME = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(\.\d+)?(Z|[+-](\d{2}):(\d{2}))$/;

function isFiniteTime(value: unknown): value is string {
  if (!nonBlank(value)) return false;
  const match = RFC3339_TIME.exec(value);
  if (!match) return false;
  const [year, month, day, hour, minute, second] = match.slice(1, 7).map(Number);
  // Date.parse normalizes impossible dates (2026-02-30 becomes 2026-03-02); reject them instead.
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth) return false;
  if (hour > 23 || minute > 59 || second > 59) return false;
  if (match[9] !== undefined && (Number(match[9]) > 23 || Number(match[10]) > 59)) return false;
  return Number.isFinite(Date.parse(value));
}

/**
 * A recorded canary acceptance is complete only when the acceptance flag is true and both an
 * accepting reference and a finite acceptance time are present. This is a presentation
 * completeness check only; it never verifies the identity, signature or authority behind the
 * reference, and acceptance in this view authorizes nothing.
 */
function hasRecordedCanaryAcceptance(canary: Record<string, unknown>): boolean {
  return canary.accepted === true && nonBlank(canary.acceptedByRef) && isFiniteTime(canary.acceptedAt);
}

function hasAcceptedCanary(outcome: unknown): boolean {
  const canary = safeObject(outcome);
  return canary !== null && hasRecordedCanaryAcceptance(canary);
}

function safeObject(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function readRows(value: unknown): { rows: ObservationRow[]; wellFormed: boolean } {
  if (!Array.isArray(value)) return { rows: [], wellFormed: false };
  let wellFormed = true;
  const rows: ObservationRow[] = [];
  for (const entry of value) {
    const row = safeObject(entry);
    if (
      !row ||
      !nonBlank(row.transferId) ||
      !nonBlank(row.sourceWalletRef) ||
      !nonBlank(row.targetRef) ||
      !nonBlank(row.state)
    ) {
      wellFormed = false;
      continue;
    }
    rows.push({
      transferId: row.transferId,
      sourceWalletRef: row.sourceWalletRef,
      targetRef: row.targetRef,
      state: row.state,
    });
  }
  return { rows, wellFormed };
}

function readSourceRefs(value: unknown): { refs: string[]; wellFormed: boolean } {
  if (!Array.isArray(value)) return { refs: [], wellFormed: false };
  let wellFormed = true;
  const refs: string[] = [];
  for (const entry of value) {
    if (nonBlank(entry)) refs.push(entry);
    else wellFormed = false;
  }
  return { refs, wellFormed };
}

interface ScopeAssessment {
  invalid: boolean;
  partial: boolean;
  reasons: string[];
}

function assessScope(data: BatchReleasePlanObservation): ScopeAssessment {
  const reasons: string[] = [];
  const d = safeObject(data);
  if (!d) return { invalid: true, partial: false, reasons: ["The batch release plan observation is not readable."] };

  for (const field of ["batchId", "planId", "planDigest", "sourceSnapshotId", "scopeDigest"] as const) {
    if (!nonBlank(d[field])) reasons.push(`The ${field} binding is blank.`);
  }
  if (typeof d.planVersion !== "number" || !Number.isSafeInteger(d.planVersion) || d.planVersion <= 0) {
    reasons.push("The plan version is not a positive safe integer.");
  }

  const asset = safeObject(d.assetIdentity);
  if (
    !asset ||
    !nonBlank(asset.chain) ||
    !nonBlank(asset.network) ||
    !nonBlank(asset.contractAddress) ||
    typeof asset.decimals !== "number" ||
    !Number.isInteger(asset.decimals) ||
    asset.decimals < 0 ||
    asset.decimals > 36 ||
    !nonBlank(asset.tokenStandard) ||
    (asset.symbol !== undefined && !nonBlank(asset.symbol))
  ) {
    reasons.push("The canonical asset identity is incomplete.");
  }

  if (!isExactAmount(d.valueCap, asset?.decimals)) {
    reasons.push("The exact value cap is blank or is not a decimal within the declared scale.");
  }

  const windowStart = d.windowStart;
  const windowEnd = d.windowEnd;
  if (!isFiniteTime(windowStart) || !isFiniteTime(windowEnd)) {
    reasons.push("The plan window bounds are not finite times.");
  } else if (Date.parse(windowStart) >= Date.parse(windowEnd)) {
    reasons.push("The plan window start is not before its end.");
  }

  const sources = readSourceRefs(d.sourceWalletRefs);
  if (!sources.wellFormed) reasons.push("A declared source wallet reference is blank.");
  if (sources.refs.length === 0) reasons.push("The plan declares no source wallets.");
  if (new Set(sources.refs).size !== sources.refs.length) {
    reasons.push("A declared source wallet reference is duplicated.");
  }

  const rowRead = readRows(d.rows);
  if (!rowRead.wellFormed) reasons.push("A transfer row is missing its transfer, source, target or state value.");
  if (rowRead.rows.length === 0) reasons.push("The plan declares no transfer rows.");
  const transferIds = new Set<string>();
  const targetRefs = new Set<string>();
  for (const row of rowRead.rows) {
    if (transferIds.has(row.transferId)) reasons.push("A transfer identity is duplicated.");
    transferIds.add(row.transferId);
    if (targetRefs.has(row.targetRef)) reasons.push("A target identity is duplicated.");
    targetRefs.add(row.targetRef);
    if (!sources.refs.includes(row.sourceWalletRef)) {
      reasons.push("A transfer row names a source outside the declared scope.");
    }
  }

  const canary = d.canaryOutcome;
  if (canary !== null && canary !== undefined) {
    const c = safeObject(canary);
    if (!c || typeof c.accepted !== "boolean" || (c.accepted && !hasRecordedCanaryAcceptance(c))) {
      reasons.push("The canary evidence is malformed.");
    }
  }
  const release = d.release;
  if (release !== null && release !== undefined) {
    const r = safeObject(release);
    if (!r || !nonBlank(r.releasedByRef) || !isFiniteTime(r.releasedAt) || !nonBlank(r.approvalRef)) {
      reasons.push("The release evidence is malformed.");
    } else if (!hasAcceptedCanary(canary)) {
      reasons.push("A release decision is recorded without a recorded canary acceptance.");
    }
  }

  const coverage = new Map<string, number>();
  for (const ref of sources.refs) coverage.set(ref, 0);
  for (const row of rowRead.rows) coverage.set(row.sourceWalletRef, (coverage.get(row.sourceWalletRef) ?? 0) + 1);
  const fullyCovered = [...coverage.values()].every((count) => count === 1);
  const partial = d.hasMore !== false || !fullyCovered;

  return { invalid: reasons.length > 0, partial, reasons: [...new Set(reasons)] };
}

function Fact({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-semibold uppercase tracking-wide text-muted">{label}</dt>
      <dd className="mt-0.5 break-all font-mono text-xs text-ink">{value}</dd>
    </div>
  );
}

function EvidenceText({ children }: { children: ReactNode }) {
  return <span className="text-xs font-semibold text-ink">{children}</span>;
}

function CanaryEvidence({ outcome }: { outcome: BatchReleasePlanObservation["canaryOutcome"] }) {
  const canary = safeObject(outcome);
  if (!canary || typeof canary.accepted !== "boolean" || (canary.accepted && !hasRecordedCanaryAcceptance(canary))) {
    return (
      <div className="mb-2">
        <EvidenceText>Canary acceptance not recorded</EvidenceText>
      </div>
    );
  }
  if (!canary.accepted) {
    return (
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <Badge variant="warning">Canary not accepted</Badge>
        {nonBlank(canary.reasonCode) && <span className="break-all font-mono text-xs">{canary.reasonCode}</span>}
      </div>
    );
  }
  return (
    <div className="mb-2 flex flex-wrap items-center gap-2">
      <Badge variant="neutral">Canary acceptance recorded</Badge>
      {nonBlank(canary.acceptedByRef) && (
        <span className="break-all font-mono text-xs">{canary.acceptedByRef}</span>
      )}
      {nonBlank(canary.acceptedAt) && <span className="break-all font-mono text-xs">{canary.acceptedAt}</span>}
      {nonBlank(canary.reasonCode) && <span className="break-all font-mono text-xs">{canary.reasonCode}</span>}
    </div>
  );
}

function ReleaseEvidence({
  release,
  canaryAccepted,
}: {
  release: BatchReleasePlanObservation["release"];
  canaryAccepted: boolean;
}) {
  const receipt = safeObject(release);
  if (
    !receipt ||
    !nonBlank(receipt.releasedByRef) ||
    !isFiniteTime(receipt.releasedAt) ||
    !nonBlank(receipt.approvalRef)
  ) {
    return (
      <div className="mb-2">
        <EvidenceText>Release decision not recorded</EvidenceText>
      </div>
    );
  }
  return (
    <div className="mb-2 flex flex-wrap items-center gap-2">
      <Badge variant="neutral">Release decision recorded</Badge>
      {!canaryAccepted && <Badge variant="danger">Inconsistent: no recorded canary acceptance</Badge>}
      <span className="break-all font-mono text-xs">{receipt.releasedByRef}</span>
      <span className="break-all font-mono text-xs">{receipt.releasedAt}</span>
      <span className="break-all font-mono text-xs">{receipt.approvalRef}</span>
    </div>
  );
}

function ObservedPlan({ data }: { data: BatchReleasePlanObservation }) {
  const assessment = assessScope(data);
  const raw = safeObject(data);
  if (!raw) {
    return <Badge variant="danger">BATCH_SCOPE_INVALID</Badge>;
  }
  const d = raw as unknown as Partial<BatchReleasePlanObservation>;
  const asset = safeObject(d.assetIdentity);
  const sources = readSourceRefs(d.sourceWalletRefs);
  const rowRead = readRows(d.rows);
  const decimals = asset?.decimals;
  const cap = d.valueCap;
  const windowStart = d.windowStart;
  const windowEnd = d.windowEnd;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        {assessment.invalid ? (
          <Badge variant="danger">BATCH_SCOPE_INVALID</Badge>
        ) : assessment.partial ? (
          <Badge variant="warning">BATCH_SCOPE_PARTIAL</Badge>
        ) : (
          <span className="text-xs text-muted">Scope observations received as supplied.</span>
        )}
      </div>
      {assessment.reasons.length > 0 && (
        <ul className="max-w-3xl list-disc space-y-1 pl-5 text-xs text-muted">
          {assessment.reasons.map((reason) => (
            <li key={reason}>{reason}</li>
          ))}
        </ul>
      )}

      <div className="rounded-lg border border-line bg-surface px-4 py-4">
        <h3 className="mb-3 text-xs font-bold uppercase tracking-wide text-muted">Plan and snapshot bindings</h3>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
          {nonBlank(d.batchId) && <Fact label="Batch" value={d.batchId} />}
          {nonBlank(d.planId) && <Fact label="Plan" value={d.planId} />}
          {typeof d.planVersion === "number" && Number.isSafeInteger(d.planVersion) && (
            <Fact label="Plan version" value={d.planVersion} />
          )}
          {nonBlank(d.planDigest) && <Fact label="Plan digest" value={d.planDigest} />}
          {nonBlank(d.sourceSnapshotId) && <Fact label="Source snapshot" value={d.sourceSnapshotId} />}
          {nonBlank(d.scopeDigest) && <Fact label="Scope digest" value={d.scopeDigest} />}
        </dl>
      </div>

      <div className="rounded-lg border border-line bg-surface px-4 py-4">
        <h3 className="mb-3 text-xs font-bold uppercase tracking-wide text-muted">Canonical asset and exact cap</h3>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
          {nonBlank(asset?.chain) && <Fact label="Chain" value={asset?.chain} />}
          {nonBlank(asset?.network) && <Fact label="Network" value={asset?.network} />}
          {nonBlank(asset?.contractAddress) && <Fact label="Contract address" value={asset?.contractAddress} />}
          {nonBlank(asset?.tokenStandard) && <Fact label="Token standard" value={asset?.tokenStandard} />}
          {typeof decimals === "number" && Number.isInteger(decimals) && decimals >= 0 && decimals <= 36 && (
            <Fact label="Decimals" value={decimals} />
          )}
          {nonBlank(asset?.symbol) && <Fact label="Symbol" value={asset?.symbol} />}
          {isExactAmount(cap, decimals) && <Fact label="Exact value cap" value={cap} />}
          {isFiniteTime(windowStart) && <Fact label="Window start" value={windowStart} />}
          {isFiniteTime(windowEnd) && <Fact label="Window end" value={windowEnd} />}
        </dl>
      </div>

      {sources.refs.length > 0 && (
        <div className="rounded-lg border border-line bg-surface px-4 py-4">
          <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-muted">Declared source wallets</h3>
          <p className="text-xs text-muted">
            One row per declared source is required before this scope is fully represented.
          </p>
          <ul className="mt-2 space-y-1">
            {sources.refs.map((ref, index) => (
              <li key={`${index}-${ref}`} className="break-all font-mono text-xs text-ink">
                {ref}
              </li>
            ))}
          </ul>
        </div>
      )}

      {rowRead.wellFormed && rowRead.rows.length > 0 && (
        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <th>Transfer</th>
                <th>Source wallet</th>
                <th>Target</th>
                <th>State</th>
              </tr>
            </thead>
            <tbody>
              {rowRead.rows.map((row, index) => (
                <tr key={`${index}-${row.transferId}`}>
                  <td data-label="Transfer" className="break-all font-mono text-xs">{row.transferId}</td>
                  <td data-label="Source wallet" className="break-all font-mono text-xs">{row.sourceWalletRef}</td>
                  <td data-label="Target" className="break-all font-mono text-xs">{row.targetRef}</td>
                  <td data-label="State" className="break-all font-mono text-xs">{row.state}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrapper>
      )}

      <div className="rounded-lg border border-line bg-surface px-4 py-4">
        <h3 className="mb-3 text-xs font-bold uppercase tracking-wide text-muted">Recorded evidence</h3>
        <CanaryEvidence outcome={d.canaryOutcome ?? null} />
        <ReleaseEvidence release={d.release ?? null} canaryAccepted={hasAcceptedCanary(d.canaryOutcome)} />
        <p className="text-xs text-muted">
          Recorded canary acceptance and release decisions are observations only. They do not authorize execution,
          and this view has no release, approval, retry or signing control.
        </p>
      </div>
    </div>
  );
}

export function BatchReleasePlanView({ state }: { state: BatchReleasePlanState }) {
  return (
    <section aria-labelledby="batch-release-plan-heading" className="mt-6 mb-6">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h2 id="batch-release-plan-heading" className="text-sm font-bold text-ink">
          Batch release plan
        </h2>
        <Badge variant="warning">{EXECUTION_UNAVAILABLE}</Badge>
      </div>
      <p className="mb-4 max-w-3xl text-xs text-muted">
        This view reports a batch plan and its recorded canary and release evidence. It cannot approve a plan, accept
        a canary, sign or release a batch.
      </p>

      {state.status === "loading" && (
        <div className="rounded-lg border border-line bg-surface px-4 py-6 text-sm text-muted">
          Loading batch release plan
        </div>
      )}

      {(state.status === "unavailable" || state.status === "refused") && (
        <div className="rounded-lg border border-line bg-surface px-4 py-4">
          <div className="break-all font-mono text-xs text-ink">{state.reasonCode}</div>
          {nonBlank(state.supportReference) && (
            <div className="mt-1 break-all font-mono text-xs text-muted">{state.supportReference}</div>
          )}
        </div>
      )}

      {state.status === "observed" && <ObservedPlan data={state.data} />}
    </section>
  );
}
